/**
 * TS port ověřeného výpočetního jádra `eu.scrumware.itrader.funds.calc`
 * (portilio-be, větev feature/modul-distribuce — FundMath, UnitPricing,
 * ReplayEngine, FeeCalc). Čisté funkce, počítá se výhradně v Dec (BigInt);
 * fixtures z FundCalcTest.java jsou přeneseny do fundCalc.test.ts.
 *
 * Vzorce:
 *   cena      = NAV / total_units       (NAV vždy BEZ nových vkladů;
 *                                        při stejném dni nejdřív ocenění, pak vklady)
 *   jednotky  = vklad / cena            (nebo units_override / price_override — Šenkeřík)
 *   mgmt_fee  = NAV * sazba_pa / 12
 *   success   = max(0, units*cena_konec_roku − invested) * sazba
 *
 * KLÍČOVÉ: total_units je KUMULATIVNÍ — nikdy klouzavé okno (SEMI +2,57 % bug).
 */

import { Dec, DecInput } from './dec'

/** Jednotky a cena jednotky: numeric(28,8). */
export const UNIT_SCALE = 8
/** Peníze: numeric(28,4). */
export const MONEY_SCALE = 4

// ---- FundMath — zaokrouhlovací konvence (HALF_UP) ----

export function roundUnits(value: DecInput): Dec {
  return Dec.of(value).setScale(UNIT_SCALE)
}

export function roundMoney(value: DecInput): Dec {
  return Dec.of(value).setScale(MONEY_SCALE)
}

/** Dělení do jednotkové přesnosti (cena, počet jednotek). */
export function divideUnits(a: DecInput, b: DecInput): Dec {
  return Dec.of(a).div(b, UNIT_SCALE)
}

/** Dělení do peněžní přesnosti. */
export function divideMoney(a: DecInput, b: DecInput): Dec {
  return Dec.of(a).div(b, MONEY_SCALE)
}

// ---- UnitPricing ----

/** Cena jednotky = NAV / total_units; při total_units <= 0 vrací fallback (historicky 1.0). */
export function unitPrice(
  nav: DecInput,
  totalUnits: DecInput | null | undefined,
  fallback: DecInput
): Dec {
  const total = totalUnits == null ? null : Dec.of(totalUnits)
  if (!total || total.signum() <= 0) {
    return Dec.of(fallback)
  }
  return divideUnits(nav, total)
}

/** Počet jednotek = vklad / cena. */
export function unitsFor(amount: DecInput, price: DecInput): Dec {
  return divideUnits(amount, price)
}

// ---- ReplayEngine ----

/** Ocenění fondu k datu (NAV bez nových vkladů). Datum = ISO yyyy-MM-dd. */
export interface ValuationEvent {
  type: 'VALUATION'
  date: string
  nav: DecInput
}

/** Vklad klienta; priceOverride/unitsOverride = ruční výjimka (Šenkeřík), jinak vynecháno. */
export interface DepositEvent {
  type: 'DEPOSIT'
  date: string
  clientId: string
  amount: DecInput
  priceOverride?: DecInput | null
  unitsOverride?: DecInput | null
}

export type FundEvent = ValuationEvent | DepositEvent

export interface ReplayPosition {
  units: Dec
  invested: Dec
}

export interface ReplayResult {
  totalUnits: Dec
  price: Dec
  positions: Map<string, ReplayPosition>
}

/**
 * Přehraje ocenění + vklady (v libovolném pořadí — engine si je stabilně seřadí).
 * Tie-break při stejném datu: nejdřív ocenění, pak vklady.
 */
export function replay(events: FundEvent[], initialPrice: DecInput): ReplayResult {
  // ISO data se řadí lexikograficky; Array.sort je stabilní (jako List.sort v Javě).
  const sorted = [...events].sort((a, b) => {
    if (a.date !== b.date) {
      return a.date < b.date ? -1 : 1
    }
    return (a.type === 'VALUATION' ? 0 : 1) - (b.type === 'VALUATION' ? 0 : 1)
  })

  let totalUnits = Dec.ZERO.setScale(UNIT_SCALE)
  let price = Dec.of(initialPrice)
  const positions = new Map<string, ReplayPosition>()

  for (const event of sorted) {
    if (event.type === 'VALUATION') {
      price = unitPrice(event.nav, totalUnits, price)
    } else {
      const rowPrice = event.priceOverride != null ? Dec.of(event.priceOverride) : price
      const units =
        event.unitsOverride != null
          ? roundUnits(event.unitsOverride)
          : unitsFor(event.amount, rowPrice)
      totalUnits = roundUnits(totalUnits.add(units))
      const prev = positions.get(event.clientId) ?? {
        units: Dec.ZERO.setScale(UNIT_SCALE),
        invested: Dec.ZERO.setScale(MONEY_SCALE),
      }
      positions.set(event.clientId, {
        units: roundUnits(prev.units.add(units)),
        invested: roundMoney(prev.invested.add(event.amount)),
      })
    }
  }

  return { totalUnits, price, positions }
}

// ---- FeeCalc ----

/** Měsíční akruál manažerského fee = NAV * sazba_pa / 12. */
export function mgmtFeeMonthly(nav: DecInput, ratePa: DecInput): Dec {
  return divideMoney(Dec.of(nav).mul(ratePa), 12)
}

export interface SuccessFee {
  profit: Dec
  successFee: Dec
  feeUnits: Dec
  unitsAfter: Dec
  distribution: Dec
}

export function successFee(
  units: DecInput,
  invested: DecInput,
  yearEndPrice: DecInput,
  successFeeRate: DecInput,
  distributionRate: DecInput
): SuccessFee {
  const value = roundMoney(Dec.of(units).mul(yearEndPrice))
  const profit = roundMoney(value.sub(invested).max(Dec.ZERO))
  const fee = roundMoney(profit.mul(successFeeRate))
  const feeUnits = divideUnits(fee, yearEndPrice)
  const unitsAfter = roundUnits(Dec.of(units).sub(feeUnits))
  const distribution = roundMoney(fee.mul(distributionRate))
  return { profit, successFee: fee, feeUnits, unitsAfter, distribution }
}
